/**
 * Per-character text animation effects (anime.js stagger / GSAP SplitText
 * style). The animation runtime tweens a single `textProgress` value; every
 * per-char state here is a pure function of (progress, char index), so
 * export seeking stays deterministic.
 */

export const TEXT_EFFECTS = {
  fadeIn: 1,
  slideUp: 2,
  slideDown: 3,
  zoomIn: 4,
  rotateIn: 5,
  flipIn: 6,
  bounceIn: 7,
  dropIn: 8,
  wave: 9,
  jump: 10,
  jitter: 11,
  scramble: 12,
  typewriter: 13,
  trackingIn: 14,
  swing: 15,
  pulse: 16,
} as const;

/** Effects that loop (phase-driven) rather than settle at progress=1 —
 * used by preview UIs to pick a looping representation. */
export const LOOPING_TEXT_EFFECTS: ReadonlySet<number> = new Set([
  TEXT_EFFECTS.wave,
  TEXT_EFFECTS.jump,
  TEXT_EFFECTS.jitter,
  TEXT_EFFECTS.swing,
  TEXT_EFFECTS.pulse,
]);

export type TextEffectConfig = {
  effect: number;
  /** 0..1, tweened by the animation runtime */
  progress: number;
  /** 0..1 — how much of the duration is spread across characters */
  stagger: number;
  /** effect-specific strength multiplier, 1 = default */
  intensity: number;
  /** animation unit: 0 chars (legacy), 1 words, 2 lines */
  split?: number;
};

export const TEXT_SPLITS = { chars: 0, words: 1, lines: 2 } as const;

/**
 * Word/line splits: maps every rendered char (by wrapped line) to its
 * animation unit index, so `getTextCharState` staggers whole words or lines
 * instead of chars. Whitespace adopts the preceding unit — the renderer
 * skips it anyway. A line break ends the current word.
 */
export const buildTextUnits = (
  lines: readonly string[],
  split: number,
): { perLine: number[][]; count: number } => {
  const perLine: number[][] = [];
  if (split === TEXT_SPLITS.lines) {
    lines.forEach((line, i) => {
      perLine.push(Array.from(line, () => i));
    });
    return { perLine, count: Math.max(1, lines.length) };
  }
  // words
  let unit = -1;
  let prevWasSpace = true;
  for (const line of lines) {
    const indices: number[] = [];
    for (const ch of line) {
      const isSpace = ch.trim() === "";
      if (!isSpace && prevWasSpace) {
        unit++;
      }
      indices.push(Math.max(0, unit));
      prevWasSpace = isSpace;
    }
    perLine.push(indices);
    prevWasSpace = true; // a wrapped/hard line break ends the word
  }
  return { perLine, count: Math.max(1, unit + 1) };
};

export type TextPathGlyph = {
  x: number;
  y: number;
  /** tangent angle, radians */
  angle: number;
};

/**
 * Text on Path: glyph centers placed by cumulative advance along a flat
 * [x,y,…] polyline's arc length; `progress` slides the whole run along the
 * remaining slack. Null for degenerate outlines (fewer than 2 points or
 * near-zero length) — callers fall back to normal rendering.
 */
export const layoutTextOnPath = (
  advances: readonly number[],
  outline: readonly number[],
  progress: number,
): TextPathGlyph[] | null => {
  if (outline.length < 4) {
    return null;
  }
  const cumulative: number[] = [0];
  let total = 0;
  for (let i = 2; i < outline.length; i += 2) {
    total += Math.hypot(
      outline[i] - outline[i - 2],
      outline[i + 1] - outline[i - 1],
    );
    cumulative.push(total);
  }
  if (total < 1) {
    return null;
  }
  const pointAt = (d: number): TextPathGlyph => {
    const dist = Math.max(0, Math.min(total, d));
    let seg = 1;
    while (seg < cumulative.length - 1 && cumulative[seg] < dist) {
      seg++;
    }
    const segStart = cumulative[seg - 1];
    const segLen = cumulative[seg] - segStart || 1;
    const t = (dist - segStart) / segLen;
    const x0 = outline[(seg - 1) * 2];
    const y0 = outline[(seg - 1) * 2 + 1];
    const x1 = outline[seg * 2];
    const y1 = outline[seg * 2 + 1];
    return {
      x: x0 + (x1 - x0) * t,
      y: y0 + (y1 - y0) * t,
      angle: Math.atan2(y1 - y0, x1 - x0),
    };
  };
  const textLen = advances.reduce((s, a) => s + a, 0);
  const slack = Math.max(0, total - textLen);
  const offset = Math.max(0, Math.min(1, progress)) * slack;
  const glyphs: TextPathGlyph[] = [];
  let advance = 0;
  for (const a of advances) {
    glyphs.push(pointAt(offset + advance + a / 2));
    advance += a;
  }
  return glyphs;
};

export type TextCharState = {
  dx: number;
  dy: number;
  scaleX: number;
  scaleY: number;
  /** radians */
  rotate: number;
  /** 0..1 */
  alpha: number;
  /** replacement glyph (scramble) */
  char?: string;
};

const NEUTRAL: TextCharState = {
  dx: 0,
  dy: 0,
  scaleX: 1,
  scaleY: 1,
  rotate: 0,
  alpha: 1,
};

const SCRAMBLE_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#@$%&*+=<>?";

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

/** Deterministic pseudo-random in [0, 1) from two integers. */
const prand = (a: number, b: number) => {
  let h = (a * 374761393 + b * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
};

/** Staggered local progress: char `index` starts at (index/count)*stagger
 * and finishes within the remaining (1 - stagger) of the timeline. */
const localProgress = (
  progress: number,
  index: number,
  count: number,
  stagger: number,
) => {
  if (count <= 1) {
    return clamp01(progress);
  }
  const s = clamp01(stagger);
  const start = (index / (count - 1)) * s;
  return clamp01((progress - start) / Math.max(1 - s, 0.0001));
};

const backOut = (t: number) => {
  const c = 1.70158;
  const u = t - 1;
  return 1 + (c + 1) * u * u * u + c * u * u;
};

const bounceOut = (t: number) => {
  const n1 = 7.5625;
  const d1 = 2.75;
  if (t < 1 / d1) {
    return n1 * t * t;
  }
  if (t < 2 / d1) {
    return n1 * (t -= 1.5 / d1) * t + 0.75;
  }
  if (t < 2.5 / d1) {
    return n1 * (t -= 2.25 / d1) * t + 0.9375;
  }
  return n1 * (t -= 2.625 / d1) * t + 0.984375;
};

export const getTextCharState = (
  cfg: TextEffectConfig,
  index: number,
  count: number,
  fontSize: number,
): TextCharState => {
  const p = cfg.progress;
  const k = cfg.intensity;
  const lp = localProgress(p, index, count, cfg.stagger);

  switch (cfg.effect) {
    case TEXT_EFFECTS.fadeIn:
      return { ...NEUTRAL, alpha: lp };

    case TEXT_EFFECTS.slideUp:
      return { ...NEUTRAL, dy: (1 - lp) * 0.8 * fontSize * k, alpha: lp };

    case TEXT_EFFECTS.slideDown:
      return { ...NEUTRAL, dy: -(1 - lp) * 0.8 * fontSize * k, alpha: lp };

    case TEXT_EFFECTS.zoomIn:
      return { ...NEUTRAL, scaleX: lp, scaleY: lp, alpha: lp };

    case TEXT_EFFECTS.rotateIn:
      return {
        ...NEUTRAL,
        rotate: (1 - lp) * (Math.PI / 2) * k,
        alpha: lp,
      };

    case TEXT_EFFECTS.flipIn:
      return { ...NEUTRAL, scaleY: lp, alpha: Math.min(1, lp * 2) };

    case TEXT_EFFECTS.bounceIn: {
      const eased = backOut(lp);
      return {
        ...NEUTRAL,
        scaleX: eased,
        scaleY: eased,
        alpha: Math.min(1, lp * 4),
      };
    }

    case TEXT_EFFECTS.dropIn:
      return {
        ...NEUTRAL,
        dy: -(1 - bounceOut(lp)) * 1.5 * fontSize * k,
        alpha: Math.min(1, lp * 5),
      };

    case TEXT_EFFECTS.wave:
      return {
        ...NEUTRAL,
        dy:
          Math.sin(2 * Math.PI * (p * 2 - index * 0.15)) * 0.25 * fontSize * k,
      };

    case TEXT_EFFECTS.jump:
      return {
        ...NEUTRAL,
        dy:
          -Math.abs(Math.sin(Math.PI * (p * 2 - index * 0.12))) *
          0.35 *
          fontSize *
          k,
      };

    case TEXT_EFFECTS.jitter: {
      const tick = Math.floor(p * 30);
      return {
        ...NEUTRAL,
        dx: (prand(index, tick) - 0.5) * 0.3 * fontSize * k,
        dy: (prand(index + 1013, tick) - 0.5) * 0.3 * fontSize * k,
        rotate: (prand(index + 2027, tick) - 0.5) * 0.3 * k,
      };
    }

    case TEXT_EFFECTS.scramble: {
      if (lp >= 1) {
        return NEUTRAL;
      }
      const tick = Math.floor(p * 30);
      return {
        ...NEUTRAL,
        char: SCRAMBLE_CHARS[
          Math.floor(prand(index, tick) * SCRAMBLE_CHARS.length)
        ],
        alpha: 0.4 + 0.6 * lp,
      };
    }

    case TEXT_EFFECTS.typewriter:
      return { ...NEUTRAL, alpha: p * count > index ? 1 : 0 };

    case TEXT_EFFECTS.trackingIn:
      return {
        ...NEUTRAL,
        dx: (index - (count - 1) / 2) * (1 - clamp01(p)) * 0.6 * fontSize * k,
        alpha: Math.min(1, p * 2),
      };

    case TEXT_EFFECTS.swing:
      return {
        ...NEUTRAL,
        rotate:
          Math.sin(2 * Math.PI * (p * 2 - index * 0.1)) * 0.15 * Math.PI * k,
      };

    case TEXT_EFFECTS.pulse: {
      const scale =
        1 + Math.sin(2 * Math.PI * (p * 2 - index * 0.1)) * 0.15 * k;
      return { ...NEUTRAL, scaleX: scale, scaleY: scale };
    }

    default:
      return NEUTRAL;
  }
};
